/**
 * Settings -> Active Sessions.
 *
 * This card is what makes per-credential revocation usable: without it the backend can
 * revoke but no user can ask it to.
 */
import toast from 'react-hot-toast';
import { Card } from '../common/Card';
import { useAuth } from '../../context/AuthContext';
import { useSessions, useRevokeSession, useRevokeAllSessions } from '../../hooks/useSessions';
// Moved to utils when ConnectedApps became the second caller. Same function, same wording.
import { relativeTime as relative } from '../../utils/relativeTime';

export const ActiveSessions = () => {
  const { logout } = useAuth();
  const { data, isLoading, isError } = useSessions();
  const revoke = useRevokeSession();
  const revokeAll = useRevokeAllSessions();

  // FIX 1 (Critical): with the credential store disabled, the backend never had any rows
  // to show in the first place (see session.service.ts / auth.controller.ts). Rendering
  // this card in that state -- even the "No active sessions found" empty state -- tells a
  // plainly-signed-in user their session is gone, which is false. Render nothing at all so
  // the profile page looks exactly as it did before this feature existed.
  if (data?.enabled === false) return null;

  const sessions = data?.sessions;
  const others = (sessions ?? []).filter((s) => !s.isCurrent);

  // The backend can only exclude the caller's own session from "revoke all" when it can
  // resolve which credential that is (see session.service.ts). When it can't, it revokes
  // everything including this device and reports currentSessionKept: false. In that case the
  // caller's own session is already dead, so we must NOT just refetch the list — we redirect
  // to login instead, via the same logout() the rest of the app uses (clears cached data too).
  const handleSignOutElsewhere = () => {
    revokeAll.mutate(undefined, {
      onSuccess: (currentSessionKept) => {
        if (!currentSessionKept) {
          toast('You were signed out of all devices, including this one.', {
            icon: '⚠️',
            duration: 6000,
          });
          void logout();
        }
      },
    });
  };

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-stone-900">Active Sessions</h2>
          <p className="text-sm text-stone-500 mt-1">
            Devices and browsers currently signed in to your account
          </p>
        </div>
        {others.length > 0 && (
          <button
            type="button"
            onClick={handleSignOutElsewhere}
            disabled={revokeAll.isPending}
            className="text-sm font-medium text-red-600 hover:text-red-700 disabled:opacity-50"
          >
            Sign out everywhere else
          </button>
        )}
      </div>

      {isLoading && <p className="text-sm text-stone-500">Loading sessions…</p>}
      {isError && <p className="text-sm text-red-600">Could not load your sessions.</p>}

      {!isLoading && !isError && (
        <ul className="divide-y divide-stone-200">
          {(sessions ?? []).map((s) => (
            <li key={s.id} className="py-3 flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-stone-900">
                  {s.name ?? 'Unknown device'}
                  {s.isCurrent && (
                    <span className="ml-2 text-xs font-normal text-success-700">This device</span>
                  )}
                </p>
                <p className="text-xs text-stone-500">
                  {/* lastUsedAt is only set on token rotation, not on creation, so a freshly
                      created session (the common case right after logging in) always has a
                      null lastUsedAt. Falling back to createdAt avoids every new session
                      claiming to have "last used never". */}
                  {s.ipAddress ?? 'unknown IP'} ·{' '}
                  {s.lastUsedAt
                    ? `last used ${relative(s.lastUsedAt)}`
                    : `signed in ${relative(s.createdAt)}`}
                </p>
              </div>
              {!s.isCurrent && (
                <button
                  type="button"
                  onClick={() => revoke.mutate(s.id)}
                  disabled={revoke.isPending}
                  className="text-sm font-medium text-red-600 hover:text-red-700 disabled:opacity-50"
                >
                  Revoke
                </button>
              )}
            </li>
          ))}
          {(sessions ?? []).length === 0 && (
            <li className="py-3 text-sm text-stone-500">No active sessions found.</li>
          )}
        </ul>
      )}
    </Card>
  );
};

export default ActiveSessions;
